import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useStoreSettings } from "../hooks/useStoreSettings";
import type { StoreInput } from "@/types/models";
import {
  DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
  DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE,
} from "@/lib/whatsapp";
import { WhatsAppMessageTemplatesSection } from "./WhatsAppMessageTemplatesSection";
import { AcceptedPaymentMethodsSection } from "./AcceptedPaymentMethodsSection";
import { StoreLogoUploader } from "./StoreLogoUploader";
import {
  paymentMethodIdsFromGraphql,
  paymentMethodIdsToGraphql,
} from "@/lib/paymentMethods/graphql";
import { DEFAULT_PAYMENT_METHOD_ID, STORABLE_PAYMENT_METHOD_IDS } from "@/lib/paymentMethods";
import { optionalSlugSchema, slugInputValue } from "@/lib/slug";
import { pricingModeFromGraphql, pricingModeToGraphql } from "@/lib/pricing";

const phoneRegex = /^\+?[0-9]{10,15}$/;
const hexColorRegex = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const storeSettingsSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  slug: optionalSlugSchema,
  description: z.string().optional(),
  whatsappPhone: z
    .string()
    .min(1, "WhatsApp é obrigatório")
    .regex(phoneRegex, "WhatsApp inválido. Use formato internacional, ex: +5511999999999"),
  primaryColor: z
    .string()
    .optional()
    .refine((value) => !value || hexColorRegex.test(value), {
      message: "Cor inválida. Use formato hexadecimal, ex: #336699",
    }),
  whatsappProductMessageTemplate: z
    .string()
    .max(2000, "Máximo de 2000 caracteres")
    .optional(),
  whatsappCartMessageTemplate: z
    .string()
    .max(2000, "Máximo de 2000 caracteres")
    .optional(),
  acceptedPaymentMethods: z
    .array(z.enum(STORABLE_PAYMENT_METHOD_IDS))
    .min(1, "Selecione pelo menos uma forma de pagamento"),
  defaultMarkupPercent: z
    .string()
    .refine((val) => val === "" || /^\d+(?:,\d{1,2})?$/.test(val.trim()), {
      message: "Markup inválido. Use formato 30 ou 30,5",
    }),
  defaultTargetNetMarginPercent: z
    .string()
    .refine((val) => val === "" || /^\d+(?:,\d{1,2})?$/.test(val.trim()), {
      message: "Margem inválida. Use formato 20 ou 20,6",
    }),
  effectiveTaxPercent: z
    .string()
    .refine((val) => val === "" || /^\d+(?:,\d{1,2})?$/.test(val.trim()), {
      message: "Alíquota inválida. Use formato 10,7",
    }),
  defaultPricingMode: z.enum(["markup_on_cost", "target_net_margin"] as const),
  analyticsWindowDays: z
    .string()
    .min(1, "Informe o período em dias")
    .refine((val) => /^\d+$/.test(val.trim()), {
      message: "Use um número inteiro de dias",
    })
    .refine((val) => {
      const days = Number.parseInt(val.trim(), 10);
      return days >= 1 && days <= 365;
    }, {
      message: "Período deve ser entre 1 e 365 dias",
    }),
});

export type StoreSettingsFormValues = z.infer<typeof storeSettingsSchema>;

const defaultValues: StoreSettingsFormValues = {
  name: "",
  slug: "",
  description: "",
  whatsappPhone: "",
  primaryColor: "",
  whatsappProductMessageTemplate: "",
  whatsappCartMessageTemplate: "",
  acceptedPaymentMethods: [DEFAULT_PAYMENT_METHOD_ID, "credit_card"],
  defaultMarkupPercent: "",
  defaultTargetNetMarginPercent: "",
  effectiveTaxPercent: "",
  defaultPricingMode: "target_net_margin",
  analyticsWindowDays: "30",
};

function parseOptionalPercent(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalized = trimmed.replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function percentToInput(value?: number | null): string {
  if (value == null) return "";
  return String(value).replace(".", ",");
}

function toStoreInput(
  values: StoreSettingsFormValues,
  options: { includeMessageTemplates: boolean; includePaymentMethods: boolean; includePricing: boolean }
): StoreInput {
  const input: StoreInput = {
    name: values.name.trim(),
    description: values.description?.trim() || undefined,
    whatsappPhone: values.whatsappPhone.trim(),
    primaryColor: values.primaryColor?.trim() || undefined,
  };

  const slug = slugInputValue(values.slug);
  if (slug) input.slug = slug;

  if (options.includeMessageTemplates) {
    input.whatsappProductMessageTemplate =
      values.whatsappProductMessageTemplate?.trim() || null;
    input.whatsappCartMessageTemplate =
      values.whatsappCartMessageTemplate?.trim() || null;
  }

  if (options.includePaymentMethods) {
    input.acceptedPaymentMethods = paymentMethodIdsToGraphql(values.acceptedPaymentMethods);
  }

  if (options.includePricing) {
    input.defaultMarkupPercent = parseOptionalPercent(values.defaultMarkupPercent);
    input.defaultTargetNetMarginPercent = parseOptionalPercent(values.defaultTargetNetMarginPercent);
    input.effectiveTaxPercent = parseOptionalPercent(values.effectiveTaxPercent);
    input.defaultPricingMode = pricingModeToGraphql(values.defaultPricingMode);
    input.analyticsWindowDays = Number.parseInt(values.analyticsWindowDays.trim(), 10);
  }

  return input;
}

export function StoreSettingsForm() {
  const {
    store,
    loading,
    createStore,
    updateStore,
    publishStore,
    unpublishStore,
    saving,
    publishing,
    unpublishing,
  } = useStoreSettings();

  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isCreateMode = !store;

  const form = useForm<StoreSettingsFormValues>({
    resolver: zodResolver(storeSettingsSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!store) return;

    form.reset({
      name: store.name,
      slug: store.slug,
      description: store.description ?? "",
      whatsappPhone: store.whatsappPhone ?? "",
      primaryColor: store.primaryColor ?? "",
      whatsappProductMessageTemplate:
        store.whatsappProductMessageTemplate ?? DEFAULT_WHATSAPP_PRODUCT_MESSAGE_TEMPLATE,
      whatsappCartMessageTemplate:
        store.whatsappCartMessageTemplate ?? DEFAULT_WHATSAPP_CART_MESSAGE_TEMPLATE,
      acceptedPaymentMethods: paymentMethodIdsFromGraphql(store.acceptedPaymentMethods),
      defaultMarkupPercent: percentToInput(store.defaultMarkupPercent),
      defaultTargetNetMarginPercent: percentToInput(store.defaultTargetNetMarginPercent),
      effectiveTaxPercent: percentToInput(store.effectiveTaxPercent),
      defaultPricingMode: pricingModeFromGraphql(store.defaultPricingMode),
      analyticsWindowDays: String(store.analyticsWindowDays ?? 30),
    });
  }, [store, form]);

  const handleSubmit = (values: StoreSettingsFormValues) => {
    setActionMessage(null);
    setActionError(null);

    const input = toStoreInput(values, {
      includeMessageTemplates: !isCreateMode,
      includePaymentMethods: !isCreateMode,
      includePricing: true,
    });
    const callbacks = {
      onCompleted: () => {
        setActionMessage(isCreateMode ? "Loja criada com sucesso." : "Loja atualizada com sucesso.");
      },
      onError: (message: string) => setActionError(message),
    };

    if (isCreateMode) {
      createStore(input, callbacks);
    } else {
      updateStore(input, callbacks);
    }
  };

  const handlePublish = () => {
    setActionMessage(null);
    setActionError(null);
    publishStore({
      onCompleted: () => setActionMessage("Loja publicada com sucesso."),
      onError: (message) => setActionError(message),
    });
  };

  const handleUnpublish = () => {
    setActionMessage(null);
    setActionError(null);
    unpublishStore({
      onCompleted: () => setActionMessage("Loja despublicada. Status alterado para rascunho."),
      onError: (message) => setActionError(message),
    });
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-lg" data-testid="store-settings-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  const statusLabel =
    store?.status === "PUBLISHED"
      ? "Publicada"
      : store?.status === "DISABLED"
        ? "Desativada"
        : "Rascunho";

  return (
    <div className="max-w-lg">
      <h2 className="text-2xl font-light tracking-tight text-primary-900 dark:text-white mb-2">
        {isCreateMode ? "Criar sua loja" : "Minha loja"}
      </h2>

      {store && (
        <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
          Status:{" "}
          <span
            className={cn(
              "inline-block px-2 py-0.5 text-xs uppercase tracking-widest border",
              store.status === "PUBLISHED"
                ? "border-green-600 text-green-700 dark:text-green-400"
                : "border-primary-300 dark:border-primary-700"
            )}
          >
            {statusLabel}
          </span>
        </p>
      )}

      {actionMessage && (
        <Alert className="mb-6">
          <AlertTitle>Sucesso</AlertTitle>
          <AlertDescription>{actionMessage}</AlertDescription>
        </Alert>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription role="alert">{actionError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-6"
          data-testid="store-settings-form"
        >
          {store && <StoreLogoUploader store={store} />}

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome da loja</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug (URL{isCreateMode ? ", opcional" : ""})</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder={
                      isCreateMode
                        ? "Gerado automaticamente a partir do nome"
                        : undefined
                    }
                  />
                </FormControl>
                {!isCreateMode && (
                  <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                    Alterar o slug pode quebrar links já compartilhados. Use com cuidado.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <textarea
                    {...field}
                    rows={3}
                    className={cn(
                      "flex w-full border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 px-4 py-3 text-base font-light text-primary-950 dark:text-white transition-colors duration-300 placeholder:text-primary-400 focus-visible:outline-none focus-visible:border-primary-400"
                    )}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="whatsappPhone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>WhatsApp</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="+5511999999999" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="primaryColor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cor primária</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="#336699" />
                </FormControl>
                <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                  Formato hexadecimal, ex: #336699
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isCreateMode && <AcceptedPaymentMethodsSection />}

          <FormField
            control={form.control}
            name="defaultPricingMode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Modo de precificação sugerida</FormLabel>
                <FormControl>
                  <select
                    {...field}
                    className={cn(
                      "flex h-10 w-full border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-950 px-4 py-2 text-base font-light text-primary-950 dark:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:border-primary-400"
                    )}
                  >
                    <option value="target_net_margin">Margem líquida sobre preço final</option>
                    <option value="markup_on_cost">Markup sobre custo</option>
                  </select>
                </FormControl>
                <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                  Padrão da loja para calcular preço sugerido. Cada produto pode sobrescrever na
                  edição.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultMarkupPercent"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Markup padrão sobre custo (%)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: 30" inputMode="decimal" />
                </FormControl>
                <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                  Padrão da loja. Produtos sem markup próprio herdam este valor.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="defaultTargetNetMarginPercent"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Margem líquida desejada (%)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: 20,6" inputMode="decimal" />
                </FormControl>
                <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                  Padrão da loja quando o modo é margem líquida. Produtos podem sobrescrever.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="effectiveTaxPercent"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Alíquota efetiva de impostos (%)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: 10,7" inputMode="decimal" />
                </FormControl>
                <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                  Padrão da loja para impostos embutidos. Produtos podem definir alíquota própria.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="analyticsWindowDays"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Período de análises (dias)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Ex: 30" inputMode="numeric" />
                </FormControl>
                <p className="text-xs font-light text-primary-500 dark:text-primary-500">
                  Quantidade de dias exibidos no painel de análises da loja.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isCreateMode && <WhatsAppMessageTemplatesSection />}

          <Button type="submit" disabled={saving} className="w-full sm:w-auto">
            {saving
              ? "SALVANDO..."
              : isCreateMode
                ? "CRIAR LOJA"
                : "SALVAR ALTERAÇÕES"}
          </Button>
        </form>
      </Form>

      {store && (
        <div className="mt-8 pt-8 border-t border-primary-200 dark:border-primary-800 flex flex-wrap gap-3">
          {store.status === "DRAFT" && (
            <Button
              type="button"
              variant="default"
              disabled={publishing}
              onClick={handlePublish}
            >
              {publishing ? "PUBLICANDO..." : "PUBLICAR LOJA"}
            </Button>
          )}
          {store.status === "PUBLISHED" && (
            <Button
              type="button"
              variant="outline"
              disabled={unpublishing}
              onClick={handleUnpublish}
            >
              {unpublishing ? "DESPUBLICANDO..." : "DESPUBLICAR LOJA"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
